import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import integrationService from '../../services/api/integrationService';
import DanlonPayTypeSelect from './DanlonPayTypeSelect';

const Card = styled.section`background:#fff; border-radius:8px; padding:24px; margin-bottom:24px; box-shadow:0 1px 3px #0002; scroll-margin-top:90px; color:var(--ve-ink-800); h2 { margin-top:0; font-size:1.15rem; } h3 { font-size:1rem; } p,small { line-height:1.5; }`;
const Grid = styled.div`display:grid; grid-template-columns:repeat(auto-fit,minmax(min(100%,260px),1fr)); gap:18px; margin:16px 0;`;
const Field = styled.label`display:flex; flex-direction:column; gap:6px; margin:10px 0; font-size:.85rem; font-weight:600; input,select,textarea { width:100%; box-sizing:border-box; padding:9px; border:1px solid var(--border-subtle); border-radius:4px; background:#fff; color:var(--ve-ink-800); font:inherit; font-weight:400; }`;
const Actions = styled.div`display:flex; flex-wrap:wrap; gap:10px; align-items:center; margin:16px 0;`;
const Button = styled.button`padding:10px 14px; border:1px solid var(--border-subtle); border-radius:4px; background:${({ $primary }) => $primary ? 'var(--act-primary)' : '#fff'}; color:${({ $primary }) => $primary ? '#fff' : 'var(--ve-ink-800)'}; font-weight:600; cursor:pointer; &:disabled { opacity:.5; cursor:default; }`;
const Item = styled.div`border:1px solid var(--border-subtle); border-radius:6px; padding:14px; min-width:0; details { margin-top:12px; font-size:.8rem; } summary { cursor:pointer; }`;
const Message = styled.p`padding:10px; background:${({ $error }) => $error ? '#fff1f0' : '#eef7ed'}; overflow-wrap:anywhere;`;

export const PAYROLL_ITEMS = {
  salary: ['hours', 'taxable'], premium: ['amount', 'taxable'],
  holiday_pay: ['amount', 'taxable'],
  contract_bonus: ['amount', 'taxable'],
  salary_advance: ['amount', 'net_addition'], premium_advance: ['amount', 'net_addition'],
  advance_repayment: ['amount', 'net_deduction'], forudbetaling: ['amount', 'net_deduction'],
  refund: ['amount', 'net_deduction'], holiday: ['days', 'informational'],
};

export default function DanlonPayrollSettings({ integration, onIntegrationSaved }) {
  const { t } = useTranslation();
  const tr = (key, options) => t('admin.integrations.payrollSetup.' + key, options);
  const [employers, setEmployers] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [cvr, setCvr] = useState(integration?.config?.companyCvr || '');
  const [draft, setDraft] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [history, setHistory] = useState(null);
  const selectedCvr = integration?.config?.companyCvr || '';
  useEffect(() => {
    let active = true;
    Promise.all([integrationService.listDanlonPayrollEmployers(), integrationService.getDanlonSupportedPayTypes()])
      .then(([list, supported]) => { if (active) { setEmployers(list.employers || []); setCatalog(supported); } })
      .catch((err) => { if (active) setError(err.message); });
    return () => { active = false; };
  }, []);
  const run = async (action) => {
    setBusy(true); setError(''); setNotice('');
    try { await action(); } catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  const open = () => run(async () => {
    const next = await integrationService.getDanlonPayrollSettings(cvr.trim());
    setDraft(next); setReason(''); setHistory(null);
  });
  const changeMapping = (item, patch) => setDraft((prev) => {
    const existing = prev.selectionMappings?.find((mapping) => mapping.item === item && mapping.sourceMonth === null && mapping.active);
    return { ...prev, selectionMappings: [
      ...(prev.selectionMappings || []).filter((mapping) => mapping.item !== item),
      { item, sourceMonth: null, amountMultiplier: 1, active: true, ...existing, ...patch },
    ] };
  });
  const save = (event) => {
    event.preventDefault();
    run(async () => {
      const mappings = (draft.selectionMappings || []).filter((row) => row.active && row.payTypeId).map((row) => ({
        item: row.item, sourceMonth: null, payTypeId: row.payTypeId,
        ...(row.customNumber !== undefined ? { customNumber: row.customNumber } : {}),
        amountMultiplier: row.amountMultiplier, ...(row.payslipLabel ? { payslipLabel: row.payslipLabel } : {}),
      }));
      const next = await integrationService.saveDanlonMappings(draft.employerCvr, {
        expectedRevision: draft.revision, employerName: draft.employerName, mappings, ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
      setDraft(next); setReason(''); setNotice(tr('saved'));
      const list = await integrationService.listDanlonPayrollEmployers(); setEmployers(list.employers || []);
    });
  };

  return <Card id="danlon-payroll-settings" aria-labelledby="danlon-payroll-title">
    <h2 id="danlon-payroll-title">{tr('title')}</h2>
    <p>{tr('description')}</p>
    <p><strong>{tr('selectedEmployer')}: {selectedCvr || tr('noneSelected')}</strong></p>
    {error && <Message $error role="alert">{error}</Message>}
    {notice && <Message role="status">{notice}</Message>}
    <Field>{tr('employerCvr')}<input inputMode="numeric" maxLength={8} value={cvr} disabled={busy} onChange={(event) => setCvr(event.target.value)} list="danlon-employers" /></Field>
    <datalist id="danlon-employers">{employers.map((entry) => <option key={entry.employerCvr} value={entry.employerCvr}>{entry.employerName}</option>)}</datalist>
    <Actions><Button disabled={busy || !catalog || !/^\d{8}$/.test(cvr.trim())} onClick={open}>{tr('openEmployer')}</Button></Actions>
    {draft && catalog && <>
      <h3>{tr('editingEmployer', { cvr: draft.employerCvr })}</h3>
      <Actions><Button disabled={busy || !draft.revision || selectedCvr === draft.employerCvr} onClick={() => run(async () => {
        const updated = await integrationService.updateIntegration('danlon', { config: { companyCvr: draft.employerCvr } });
        onIntegrationSaved(updated); setNotice(tr('selectedSaved'));
      })}>{tr('useEmployer')}</Button></Actions>
      {!draft.revision && <p>{tr('saveBeforeSelection')}</p>}
      <form onSubmit={save}>
        <Field>{tr('employerName')}<input value={draft.employerName} required disabled={busy} maxLength={200} onChange={(event) => setDraft({ ...draft, employerName: event.target.value })} /></Field>
        <h3>{tr('mappingsTitle')}</h3><p>{tr('mappingsHelp')}</p>
        <Grid>{Object.entries(PAYROLL_ITEMS).map(([item, [valueKind, treatment]]) => {
          const mapping = draft.selectionMappings?.find((row) => row.item === item && row.sourceMonth === null && row.active)
            || { item, payTypeId: '', amountMultiplier: 1 };
          const label = tr('items.' + item);
          return <Item key={item}>
            <DanlonPayTypeSelect catalog={catalog} label={label} value={mapping} item={item} valueKind={valueKind}
              deduction={treatment === 'net_deduction'} disabled={busy} onChange={(patch) => changeMapping(item, patch)} />
            {mapping.payTypeId && <details><summary>{tr('details')}</summary>
              <Field>{tr('label')}<input aria-label={label + ' — ' + tr('label')} disabled={busy} maxLength={30} value={mapping.payslipLabel || ''}
                onChange={(event) => changeMapping(item, { payslipLabel: event.target.value })} /><small>{tr('labelHelp')}</small></Field>
            </details>}
          </Item>;
        })}</Grid>
        <p>{tr('earlierEarningsHelp')}</p>
        <p>{tr('historyPreservedHelp')}</p>
        <details><summary>{tr('reasonOptional')}</summary><Field>{tr('reason')}<textarea value={reason} disabled={busy} maxLength={1000} onChange={(event) => setReason(event.target.value)} /></Field></details>
        <Actions><Button $primary type="submit" disabled={busy}>{tr('save')}</Button></Actions>
      </form>
      <a href={catalog.sourceUrl} target="_blank" rel="noopener noreferrer">{tr('supportedTypesSource')}</a>
      <Actions><Button disabled={busy || !draft.revision} onClick={() => run(async () => { const result = await integrationService.getDanlonPayrollHistory(draft.employerCvr); setHistory(result.revisions); })}>{tr('history')}</Button></Actions>
      {history && <ul>{history.map((entry) => <li key={entry.revision}>{entry.createdAt ? new Date(entry.createdAt).toLocaleString() : entry.revision} — {entry.reason}</li>)}</ul>}
    </>}
  </Card>;
}
